import React, { useState, useEffect } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { SearchableSelect } from "./ui/searchable-select";
import { toast } from "sonner";
import api from "../utils/api";
import { formatMXN } from "../utils/format";

const PAYMENT_METHODS = ["efectivo", "transferencia", "tarjeta", "cheque", "otro"];

export default function SaleForm({ sale, onClose, onSave }) {
  const [form, setForm] = useState({
    type: "cotizacion", customer_id: "", customer_name: "", customer_address: "",
    customer_phone: "", customer_rfc: "", items: [], subtotal: 0, discount: 0,
    tax_rate: 0, tax_amount: 0, shipping_cost: 0, total: 0, notes: "",
    payment_method: "efectivo", status: "confirmado", valid_until: "",
    show_queue_position: true, sales_channel: ""
  });
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [saving, setSaving] = useState(false);
  const [queueCount, setQueueCount] = useState(0);

  const DRAFT_KEY = "sale_form_draft";

  useEffect(() => {
    Promise.all([
      api.get("/customers").then((r) => setCustomers(r.data)),
      api.get("/products").then((r) => setProducts(r.data)),
      api.get("/price-lists").then((r) => setPriceLists(r.data)),
      api.get("/sales/queue-count").then((r) => setQueueCount(r.data.count)).catch(() => {}),
    ]);
    if (sale) {
      setForm({ ...sale, show_queue_position: sale.show_queue_position !== false });
    } else {
      // Check for saved draft
      try {
        const draft = localStorage.getItem(DRAFT_KEY);
        if (draft) {
          const parsed = JSON.parse(draft);
          if (parsed && parsed.items && parsed.items.length > 0) {
            setForm(parsed);
            toast.info("Borrador recuperado. Si prefieres empezar de cero, haz clic en Cancelar y vuelve a abrir.");
            return;
          }
        }
      } catch {}
      // Default valid_until to 15 days from now for cotizaciones
      const defaultValidUntil = new Date();
      defaultValidUntil.setDate(defaultValidUntil.getDate() + 15);
      setForm(f => ({ ...f, valid_until: defaultValidUntil.toISOString().split('T')[0] }));
    }
  }, [sale]);

  // Auto-save draft to localStorage (only for new sales)
  useEffect(() => {
    if (!sale && form.items.length > 0) {
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch {}
    }
  }, [form, sale]);

  const calcTotals = (items, discount, taxRate, shippingCost = 0) => {
    const sub = items.reduce((a, i) => a + (i.subtotal || 0), 0);
    const discAmt = sub * (discount / 100);
    const taxable = sub - discAmt;
    const taxAmt = taxable * (taxRate / 100);
    const total = taxable + taxAmt + (parseFloat(shippingCost) || 0);
    return { subtotal: sub, tax_amount: taxAmt, total };
  };

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    // When quantity changes, re-check price tier
    if (field === "quantity" && items[idx].product_id && form.customer_id) {
      const newQty = parseFloat(value) || 1;
      const tierPrice = getPriceForProduct(items[idx].product_id, newQty);
      if (tierPrice > 0) items[idx].unit_price = tierPrice;
    }
    if (field === "quantity" || field === "unit_price" || field === "discount") {
      const qty = parseFloat(items[idx].quantity) || 0;
      const price = parseFloat(items[idx].unit_price) || 0;
      const disc = parseFloat(items[idx].discount) || 0;
      items[idx].subtotal = qty * price * (1 - disc / 100);
    }
    const totals = calcTotals(items, form.discount, form.tax_rate, form.shipping_cost);
    setForm((f) => ({ ...f, items, ...totals }));
  };

  const addItem = () => {
    setForm((f) => ({
      ...f, items: [...f.items, {
        product_id: "", product_name: "", quantity: 1,
        unit: "kg", unit_price: 0, discount: 0, subtotal: 0, presentation: ""
      }]
    }));
  };

  const removeItem = (idx) => {
    const items = form.items.filter((_, i) => i !== idx);
    const totals = calcTotals(items, form.discount, form.tax_rate, form.shipping_cost);
    setForm((f) => ({ ...f, items, ...totals }));
  };

  // Get price from customer's price list (if assigned), fallback to product.price
  const getPriceForProduct = (productId, qty = 1, custId = null) => {
    const cid = custId || form.customer_id;
    if (cid) {
      const cust = customers.find(c => c.id === cid);
      if (cust?.price_list_id) {
        const pl = priceLists.find(p => p.id === cust.price_list_id);
        if (pl) {
          const plItem = pl.items?.find(i => i.product_id === productId);
          if (plItem?.tiers?.length) {
            // Find matching tier by quantity
            const sorted = [...plItem.tiers].sort((a, b) => a.min_qty - b.min_qty);
            let matched = sorted[0]; // default to first tier
            for (const tier of sorted) {
              if (qty >= tier.min_qty && (!tier.max_qty || qty <= tier.max_qty)) {
                matched = tier;
              }
            }
            if (matched) return matched.price;
          }
        }
      }
    }
    const prod = products.find(p => p.id === productId);
    return prod?.price || 0;
  };

  const selectProduct = (idx, productId) => {
    const prod = products.find((p) => p.id === productId);
    if (!prod) return;
    const items = [...form.items];
    const qty = items[idx].quantity || 1;
    const price = getPriceForProduct(productId, qty);
    items[idx] = {
      ...items[idx], product_id: prod.id, product_name: prod.name,
      unit: prod.unit, unit_price: price,
      subtotal: qty * price
    };
    const totals = calcTotals(items, form.discount, form.tax_rate, form.shipping_cost);
    setForm((f) => ({ ...f, items, ...totals }));
  };

  const selectCustomer = (customerId) => {
    const cust = customers.find((c) => c.id === customerId);
    if (!cust) return;
    // Update items prices if customer has a price list
    let updatedItems = form.items;
    if (cust.price_list_id) {
      const pl = priceLists.find(p => p.id === cust.price_list_id);
      if (pl) {
        updatedItems = form.items.map(item => {
          if (!item.product_id) return item;
          const plItem = pl.items?.find(i => i.product_id === item.product_id);
          if (plItem?.tiers?.length) {
            const qty = parseFloat(item.quantity) || 1;
            const sorted = [...plItem.tiers].sort((a, b) => a.min_qty - b.min_qty);
            let matched = sorted[0];
            for (const tier of sorted) {
              if (qty >= tier.min_qty && (!tier.max_qty || qty <= tier.max_qty)) matched = tier;
            }
            if (matched) {
              const newPrice = matched.price;
              return { ...item, unit_price: newPrice, subtotal: qty * newPrice * (1 - (parseFloat(item.discount) || 0) / 100) };
            }
          }
          return item;
        });
      }
    }
    const totals = calcTotals(updatedItems, form.discount, form.tax_rate, form.shipping_cost);
    setForm((f) => ({
      ...f, customer_id: cust.id, customer_name: cust.name,
      customer_address: cust.address, customer_phone: cust.phone,
      customer_rfc: cust.rfc, items: updatedItems, ...totals
    }));
    if (cust.price_list_id) {
      const pl = priceLists.find(p => p.id === cust.price_list_id);
      if (pl) toast.info(`Lista de precios "${pl.name}" aplicada`);
    }
  };

  const updateDiscount = (val) => {
    const discount = parseFloat(val) || 0;
    const totals = calcTotals(form.items, discount, form.tax_rate, form.shipping_cost);
    setForm((f) => ({ ...f, discount, ...totals }));
  };

  const updateTaxRate = (val) => {
    const taxRate = parseFloat(val) || 0;
    const totals = calcTotals(form.items, form.discount, taxRate, form.shipping_cost);
    setForm((f) => ({ ...f, tax_rate: taxRate, ...totals }));
  };

  const updateShipping = (val) => {
    const shipping = parseFloat(val) || 0;
    const totals = calcTotals(form.items, form.discount, form.tax_rate, shipping);
    setForm((f) => ({ ...f, shipping_cost: shipping, ...totals }));
  };

  const handleSave = async () => {
    if (form.items.length === 0) { toast.error("Agrega al menos un producto"); return; }
    if (form.items.some(it => !it.product_id)) { toast.error("Selecciona un producto en cada linea"); return; }
    setSaving(true);
    try {
      // Clean payload - only send Sale model fields
      const cleanItems = form.items.map(it => ({
        product_id: it.product_id, product_name: it.product_name || "",
        quantity: parseFloat(it.quantity) || 0, unit: it.unit || "pza",
        unit_price: parseFloat(it.unit_price) || 0, discount: parseFloat(it.discount) || 0,
        subtotal: parseFloat(it.subtotal) || 0,
      }));
      const payload = {
        type: form.type || "pedido", customer_id: form.customer_id || null,
        customer_name: form.customer_name || "", customer_address: form.customer_address || "",
        customer_phone: form.customer_phone || "", customer_rfc: form.customer_rfc || "",
        items: cleanItems, subtotal: parseFloat(form.subtotal) || 0,
        discount: parseFloat(form.discount) || 0, tax_rate: parseFloat(form.tax_rate) || 0,
        tax_amount: parseFloat(form.tax_amount) || 0, total: parseFloat(form.total) || 0,
        notes: form.notes || "", payment_method: form.payment_method || "efectivo",
        status: form.status || "confirmado", related_id: form.related_id || null,
        payments: form.payments || [], valid_until: form.valid_until || null,
        shipping_cost: parseFloat(form.shipping_cost) || 0,
      };
      if (sale) {
        await api.put(`/sales/${sale.id}`, payload);
        toast.success("Documento actualizado");
      } else {
        await api.post("/sales", payload);
        toast.success("Documento creado");
      }
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      onSave();
    } catch (err) {
      console.error("Save sale error:", err);
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(", ")
        : "Error al guardar";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={() => { try { localStorage.removeItem(DRAFT_KEY); } catch {} onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="sale-form">
        <DialogHeader>
          <DialogTitle>{sale ? "Editar documento" : "Nuevo documento"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Type & Status */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Tipo de documento</label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="sale-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cotizacion">Cotización</SelectItem>
                  <SelectItem value="pedido">Pedido</SelectItem>
                  <SelectItem value="factura">Factura</SelectItem>
                  <SelectItem value="ticket">Ticket de Venta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Estatus</label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="borrador">Borrador</SelectItem>
                  <SelectItem value="confirmado">Confirmado</SelectItem>
                  <SelectItem value="pagado">Pagado</SelectItem>
                  <SelectItem value="cancelado">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Forma de pago</label>
              <Select value={form.payment_method} onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Canal de venta</label>
              <Select value={form.sales_channel || "directo"} onValueChange={(v) => setForm((f) => ({ ...f, sales_channel: v }))}>
                <SelectTrigger data-testid="sales-channel-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="directo">Cliente directo</SelectItem>
                  <SelectItem value="redes_sociales">Redes sociales</SelectItem>
                  <SelectItem value="expo">Expo / Evento</SelectItem>
                  <SelectItem value="menudeo">Menudeo</SelectItem>
                  <SelectItem value="mayoreo">Mayoreo</SelectItem>
                  <SelectItem value="tienda_linea">Tienda en linea</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(form.type === "cotizacion") && (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Válido hasta</label>
                <input
                  type="date"
                  value={form.valid_until || ""}
                  onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))}
                  className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            )}
          </div>

          {/* Queue Position Info */}
          {(form.type === "cotizacion" || form.type === "pedido") && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-amber-600 text-lg">📋</span>
                  <div>
                    <div className="text-sm font-semibold text-amber-800">Posición en la fila</div>
                    <div className="text-xs text-amber-600">
                      Actualmente hay <strong>{queueCount} pedidos</strong> en la cola de producción
                    </div>
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.show_queue_position}
                    onChange={(e) => setForm((f) => ({ ...f, show_queue_position: e.target.checked }))}
                    className="w-4 h-4 text-amber-600 rounded border-amber-300 focus:ring-amber-500"
                  />
                  <span className="text-xs text-amber-700">Mostrar en documento</span>
                </label>
              </div>
              {form.show_queue_position && (
                <div className="mt-2 text-xs text-amber-700 bg-amber-100 rounded px-2 py-1">
                  💡 El cliente verá: "Hay {queueCount} pedidos antes del tuyo"
                </div>
              )}
            </div>
          )}

          {/* Customer */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-slate-700">Datos del cliente</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Cliente (opcional)</label>
                <SearchableSelect
                  options={customers.sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((c) => ({ value: c.id, label: c.name }))}
                  value={form.customer_id || ""}
                  onValueChange={selectCustomer}
                  placeholder="Seleccionar cliente"
                  searchPlaceholder="Buscar cliente..."
                  testId="sale-customer-search"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Nombre</label>
                <input value={form.customer_name} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))}
                  className="w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                  placeholder="Nombre del cliente" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Teléfono</label>
                <input value={form.customer_phone} onChange={(e) => setForm((f) => ({ ...f, customer_phone: e.target.value }))}
                  className="w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" placeholder="Teléfono" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">RFC</label>
                <input value={form.customer_rfc} onChange={(e) => setForm((f) => ({ ...f, customer_rfc: e.target.value }))}
                  className="w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" placeholder="RFC" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-slate-500 mb-1">Dirección</label>
                <input value={form.customer_address} onChange={(e) => setForm((f) => ({ ...f, customer_address: e.target.value }))}
                  className="w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" placeholder="Dirección" />
              </div>
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700">Productos</h3>
              <button onClick={addItem}
                className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 font-medium">
                <Plus size={14} /> Agregar producto
              </button>
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs text-slate-500 font-semibold">Producto</th>
                    <th className="text-left px-3 py-2 text-xs text-slate-500 font-semibold w-24">Presentacion</th>
                    <th className="text-left px-3 py-2 text-xs text-slate-500 font-semibold w-20">Cant.</th>
                    <th className="text-left px-3 py-2 text-xs text-slate-500 font-semibold w-16">Unidad</th>
                    <th className="text-left px-3 py-2 text-xs text-slate-500 font-semibold w-28">Precio</th>
                    <th className="text-left px-3 py-2 text-xs text-slate-500 font-semibold w-20">Desc %</th>
                    <th className="text-left px-3 py-2 text-xs text-slate-500 font-semibold w-28">Subtotal</th>
                    <th className="px-3 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((item, idx) => (
                    <tr key={idx} className="border-t border-slate-100">
                      <td className="px-3 py-2">
                        <SearchableSelect
                          options={products.filter((p) => p.category === "producto_final").sort((a, b) => a.name.localeCompare(b.name)).map((p) => ({ value: p.id, label: p.name }))}
                          value={item.product_id}
                          onValueChange={(v) => selectProduct(idx, v)}
                          placeholder="Seleccionar..."
                          searchPlaceholder="Buscar producto..."
                          testId={`sale-product-search-${idx}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input value={item.presentation || ""} onChange={(e) => updateItem(idx, "presentation", e.target.value)}
                          className="w-full border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" placeholder="Ej: 100g" data-testid={`sale-item-presentation-${idx}`} />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" value={item.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                          className="w-full border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" min="0" step="0.01" />
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">{item.unit}</td>
                      <td className="px-3 py-2">
                        <input type="number" value={item.unit_price} onChange={(e) => updateItem(idx, "unit_price", e.target.value)}
                          className="w-full border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" min="0" step="0.01" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" value={item.discount} onChange={(e) => updateItem(idx, "discount", e.target.value)}
                          className="w-full border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" min="0" max="100" />
                      </td>
                      <td className="px-3 py-2 text-xs font-medium text-slate-800">{formatMXN(item.subtotal)}</td>
                      <td className="px-3 py-2">
                        <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600 transition-colors">
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {form.items.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-6 text-slate-400 text-xs">Sin productos</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Notas</label>
                <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={3} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                  placeholder="Notas adicionales..." />
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal</span>
                <span className="font-medium">{formatMXN(form.subtotal)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Descuento (%)</span>
                <input type="number" value={form.discount} onChange={(e) => updateDiscount(e.target.value)}
                  className="w-20 border border-slate-200 rounded px-2 py-0.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-brand-500" min="0" max="100" />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">IVA (%)</span>
                <input type="number" value={form.tax_rate} onChange={(e) => updateTaxRate(e.target.value)}
                  className="w-20 border border-slate-200 rounded px-2 py-0.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-brand-500" min="0" max="100"
                  placeholder="0 = sin IVA" />
              </div>
              {form.tax_amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">IVA ({form.tax_rate}%)</span>
                  <span>{formatMXN(form.tax_amount)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600 flex items-center gap-1">Envío / Flete</span>
                <input
                  type="number"
                  value={form.shipping_cost}
                  onChange={(e) => updateShipping(e.target.value)}
                  data-testid="shipping-cost-input"
                  className="w-28 border border-slate-200 rounded px-2 py-0.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-brand-500"
                  min="0" step="0.01" placeholder="0.00"
                />
              </div>
              {form.shipping_cost > 0 && (
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Envío incluido en total</span>
                  <span>{formatMXN(form.shipping_cost)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-base border-t border-slate-200 pt-2 mt-2">
                <span>Total</span>
                <span className="text-brand-700">{formatMXN(form.total)}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving} data-testid="save-sale-btn"
              className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
